import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { useAuth } from '../../contexts/AuthContext';
import BookingList from '../../components/ui/BookingList'; 
import moment from 'moment';
import {
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowPathIcon,
  ChevronUpDownIcon,
  CheckIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { showError, showSuccess } from '../../utils/toast';

const CalendarPage = () => {
  const {t} = useTranslation();
  const { apiCall } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeCalendar = async () => {
      try {
        setError(null);
        await Promise.all([loadBookings(), loadEmployees(), loadServices()]);
      } catch (err) {
        setError(err.message || 'Failed to initialize calendar');
      }
    };
    initializeCalendar();
  }, []);

  const loadBookings = async () => {
    try {
      setIsLoading(true);
      const response = await apiCall('/companies/bookings');
      if (response && Array.isArray(response.bookings)) {
        setBookings(response.bookings);
      }
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await apiCall('/companies/employees');
      if (response && Array.isArray(response.employees)) {
        setEmployees(response.employees);
      }
    } catch (error) {}
  };

  const loadServices = async () => {
    try {
      const response = await apiCall('/companies/services');
      if (response && Array.isArray(response.services)) {
        setServices(response.services.filter(s => s.is_active));
      }
    } catch (error) {}
  };

  const filteredBookings = useMemo(() => {
    return selectedEmployee === 'all' 
      ? bookings 
      : bookings.filter(b => b.employee_id === selectedEmployee || b.staff_id === selectedEmployee);
  }, [bookings, selectedEmployee]);

  const currentEmployeeLabel = useMemo(() => {
    if (selectedEmployee === 'all') return 'All Staff';
    const emp = employees.find(e => e.id === selectedEmployee);
    return emp ? (emp.first_name || emp.name) : 'All Staff';
  }, [selectedEmployee, employees]);

  const handleCreateBooking = async (data) => {
    try {
      const employeeId = selectedEmployee !== 'all'
          ? selectedEmployee
          : employees.length > 0 ? employees[0].id : null;

      if (!employeeId) {
        showSuccess(t("company.calendar.alerts.add_staff_first"));
        return;
      }

      const payload = {
        service_id: String(data.service_id),
        client_name: data.client_name,
        phone: data.phone,
        pet_name: data.pet_name || "",
        notes: data.notes || "",
        booking_date: data.booking_date,
        duration: data.duration || 60
      };

      const res = await apiCall('/companies/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res) await loadBookings(); 
    } catch (err) {
      showError(err.message || t("company.calendar.alerts.create_error"));
    }
  };

      const handleDeleteBooking = async (id) => {
        try {
          await apiCall(`/companies/bookings/${id}`, {
            method: 'DELETE'
          });

          setBookings(prev => prev.filter(b => b.id !== id));
        } catch (err) {
          console.error("Delete error:", err);
        }
      };

  const handleUpdateStatus = async (id, payload) => {
    
    try {
      await apiCall(`/companies/bookings/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      loadBookings();
    } catch (err) {
      console.error(err);
    }
  };

  if (error) return <ErrorState message={error} />;
  if (isLoading) return <LoadingState />;

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 p-4 lg:p-8 animate-in fade-in duration-700">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase italic tracking-tighter">
              {t("company.calendar.title_label")} <span className="text-slate-300">{t("company.calendar.title_label_span")}</span>
            </h1>
          <p className="text-zinc-500 mt-1 font-medium text-lg">{t("company.calendar.subtitle")}</p>
        </div>

        {/* Фильтр сотрудников */}
        <div className="flex items-center gap-2 bg-white p-2 rounded-[24px] shadow-sm border border-zinc-100">
          <div className="relative min-w-[200px]">
            <Listbox value={selectedEmployee} onChange={setSelectedEmployee}>
              <div className="relative">
                <Listbox.Button className="relative w-full flex items-center cursor-pointer rounded-xl bg-transparent py-3 pl-4 pr-10 text-left hover:bg-zinc-50 transition-all focus:outline-none">
                  <UserGroupIcon className="w-5 h-5 text-zinc-400 mr-2" />
                  <span className="block truncate text-sm font-bold text-zinc-700">{currentEmployeeLabel}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronUpDownIcon className="h-5 w-5 text-zinc-300" />
                  </span>
                </Listbox.Button>
                <Transition as={React.Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                  <Listbox.Options className="absolute z-50 mt-2 max-h-60 w-full overflow-auto rounded-2xl bg-white py-2 text-base shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    <Listbox.Option value="all" className={({ active }) => `relative cursor-pointer select-none py-3 pl-10 pr-4 ${active ? 'bg-orange-50 text-orange-900' : 'text-zinc-900'}`}>
                       {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>{t("company.calendar.filter.all_staff")}</span>
                          {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-orange-600"><CheckIcon className="h-4 w-4" /></span>}
                        </>
                      )}
                    </Listbox.Option>
                    {employees.map((emp) => (
                      <Listbox.Option key={emp.id} value={emp.id} className={({ active }) => `relative cursor-pointer select-none py-3 pl-10 pr-4 ${active ? 'bg-orange-50 text-orange-900' : 'text-zinc-900'}`}>
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>{emp.first_name || emp.name}</span>
                            {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-orange-600"><CheckIcon className="h-4 w-4" /></span>}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
        </div>
      </div>

      {/* Stats Grid — ОСТАВЛЯЕМ ОДИН РАЗ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard label={t("company.calendar.stats.total_tasks")} value={filteredBookings.length} icon={<CalendarIcon />} color="zinc" />
        <StatCard 
          label={t("company.calendar.stats.today")} 
          value={filteredBookings.filter(b => moment(b.booking_date).isSame(moment(), 'day')).length} 
          icon={<ClockIcon />} 
          color="orange" 
        />
        <StatCard label={t("company.calendar.stats.in_queue")} value={filteredBookings.filter(b => b.status === 'pending').length} icon={<ArrowPathIcon />} color="amber" />
        <StatCard label={t("company.calendar.stats.completed")} value={filteredBookings.filter(b => b.status === 'confirmed').length} icon={<CheckCircleIcon />} color="green" />
      </div>

      {/* Booking List Container — ТЕПЕРЬ ОСНОВНОЙ */}
      <div className="bg-white rounded-[40px] shadow-2xl shadow-zinc-200/40 border border-zinc-100 overflow-hidden">
        <BookingList 
          bookings={filteredBookings}
          services={services}
          onBookingCreate={handleCreateBooking}
          onBookingDelete={handleDeleteBooking}
        />
      </div>
    </div>
  );
};

// --- Sub-components (StatCard, LoadingState, ErrorState) ---
const StatCard = ({ label, value, icon, color }) => {
  const colors = {
    zinc: "text-zinc-600 bg-zinc-100",
    orange: "text-orange-600 bg-orange-50",
    amber: "text-amber-600 bg-amber-50",
    green: "text-green-600 bg-green-50",
  };

  return (
    <div className="bg-white p-7 rounded-[32px] border border-zinc-50 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
      <div className="flex items-center justify-between">
        <div className={`p-4 rounded-2xl transition-transform group-hover:rotate-12 duration-300 ${colors[color]}`}>
          {React.cloneElement(icon, { className: 'w-6 h-6' })}
        </div>
        <span className="text-4xl font-black text-zinc-900 tracking-tighter">{value}</span>
      </div>
      <div className="mt-6">
        <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em]">{label}</p>
      </div>
    </div>
  );
};

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
    <div className="w-20 h-20 border-[6px] border-zinc-50 border-t-orange-500 rounded-full animate-spin"></div>
    <p className="text-xs font-black text-zinc-400 uppercase tracking-widest animate-pulse">Syncing Hub...</p>
  </div>
);

const ErrorState = ({ message }) => (
  <div className="flex items-center justify-center min-h-[60vh] p-4">
    <div className="bg-white p-10 rounded-[40px] shadow-2xl border border-zinc-100 text-center max-w-sm">
      <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
        <XMarkIcon className="w-10 h-10" />
      </div>
      <h3 className="text-2xl font-black text-zinc-900 mb-2">Connection Lost</h3>
      <p className="text-zinc-500 mb-8 font-medium">{message}</p>
      <button onClick={() => window.location.reload()} className="w-full py-5 bg-zinc-900 text-white font-black rounded-2xl hover:bg-zinc-800 transition-all shadow-lg shadow-zinc-200">
        Retry Sync
      </button>
    </div>
  </div>
);

export default CalendarPage;